import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  teamMemberService,
  assetService,
  fundingService,
  patentService,
  technologyService
} from '../../utils/entityServices';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const TeamAssetsInputPage = () => {
  const { id } = useParams(); // Product ID from URL
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [productData, setProductData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Entities data
  const [teamMembers, setTeamMembers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [funding, setFunding] = useState([]);
  const [patents, setPatents] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  
  // Selected entities
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedFunding, setSelectedFunding] = useState([]);
  const [selectedPatents, setSelectedPatents] = useState([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState([]);
  
  // Fetch product data on component mount
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch product data
      const { data: productResult, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      
      if (productError) throw productError;
      setProductData(productResult);
      
      // Fetch all available entities
      const [
        teamMembersData,
        assetsData,
        fundingData,
        patentsData,
        technologiesData
      ] = await Promise.all([
        teamMemberService.getAll(),
        assetService.getAll(),
        fundingService.getAll(),
        patentService.getAll(),
        technologyService.getAll()
      ]);
      
      setTeamMembers(teamMembersData);
      setAssets(assetsData);
      setFunding(fundingData);
      setPatents(patentsData);
      setTechnologies(technologiesData);
      
      // Fetch selected entities for this product
      const [
        selectedTeamMembersData,
        selectedAssetsData,
        selectedFundingData,
        selectedPatentsData,
        selectedTechnologiesData
      ] = await Promise.all([
        teamMemberService.getByProductId(id),
        assetService.getByProductId(id),
        fundingService.getByProductId(id),
        patentService.getByProductId(id),
        technologyService.getByProductId(id)
      ]);
      
      setSelectedTeamMembers(selectedTeamMembersData.map(member => member.id));
      setSelectedAssets(selectedAssetsData.map(asset => asset.id));
      setSelectedFunding(selectedFundingData.map(fund => fund.id));
      setSelectedPatents(selectedPatentsData.map(patent => patent.id));
      setSelectedTechnologies(selectedTechnologiesData.map(tech => tech.id));
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);
  
  // Handle team member selection
  const handleTeamMemberToggle = (memberId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };
  
  // Handle asset selection
  const handleAssetToggle = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };
  
  // Handle funding selection
  const handleFundingToggle = (fundingId) => {
    setSelectedFunding(prev => 
      prev.includes(fundingId)
        ? prev.filter(id => id !== fundingId)
        : [...prev, fundingId]
    );
  };
  
  // Handle patent selection
  const handlePatentToggle = (patentId) => {
    setSelectedPatents(prev => 
      prev.includes(patentId)
        ? prev.filter(id => id !== patentId)
        : [...prev, patentId]
    );
  };
  
  // Handle technology selection
  const handleTechnologyToggle = (technologyId) => {
    setSelectedTechnologies(prev => 
      prev.includes(technologyId)
        ? prev.filter(id => id !== technologyId)
        : [...prev, technologyId]
    );
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      // Process team members
      await processEntityAssociations(
        teamMemberService,
        'team_member_id',
        'product_team_members',
        selectedTeamMembers
      );
      
      // Process assets
      await processEntityAssociations(
        assetService,
        'asset_id',
        'product_assets',
        selectedAssets
      );
      
      // Process funding
      await processEntityAssociations(
        fundingService,
        'funding_id',
        'product_funding',
        selectedFunding
      );
      
      // Process patents
      await processEntityAssociations(
        patentService,
        'patent_id',
        'product_patents',
        selectedPatents
      );
      
      // Process technologies
      await processEntityAssociations(
        technologyService,
        'technology_id',
        'product_technologies',
        selectedTechnologies
      );
      
      // Navigate to pitch deck input
      navigate(`/products/${id}/pitch-deck`);
    } catch (err) {
      console.error('Error saving associations:', err);
      setError('Failed to save entity associations. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Helper function to process entity associations
  const processEntityAssociations = async (service, entityIdField, tableName, selectedIds) => {
    // Get current associations
    const { data: currentAssociations, error: fetchError } = await supabase
      .from(tableName)
      .select('*')
      .eq('product_id', id);
    
    if (fetchError) throw fetchError;
    
    // Find associations to remove
    const currentIds = currentAssociations.map(assoc => assoc[entityIdField]);
    const idsToRemove = currentIds.filter(currentId => !selectedIds.includes(currentId));
    
    // Find associations to add
    const idsToAdd = selectedIds.filter(selectedId => !currentIds.includes(selectedId));
    
    // Remove associations
    for (const entityId of idsToRemove) {
      await service.removeFromProduct(id, entityId);
    }
    
    // Add new associations
    for (const entityId of idsToAdd) {
      await service.addToProduct(id, entityId);
    }
  };
  
  if (loading || isLoading) {
    return <LoadingScreen message="Loading..." />;
  }
  
  if (!productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>Product not found. Please check the URL and try again.</p>
        </div>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to={`/products/${id}/financial`} className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Financial Input
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Associate Resources with {productData.name}</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Team Members */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Team Members</h2>
            
            <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
              {teamMembers.length === 0 ? (
                <p className="text-gray-500">No team members found. Add team members in the Management section.</p>
              ) : (
                teamMembers.map(member => (
                  <div key={member.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`team-${member.id}`}
                      checked={selectedTeamMembers.includes(member.id)}
                      onChange={() => handleTeamMemberToggle(member.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor={`team-${member.id}`} className="ml-3 block text-gray-700">
                      <span className="font-medium">{member.name}</span>
                      <span className="text-sm text-gray-500 block">{member.title}</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            
            <Link to="/management/team" className="text-primary-600 hover:text-primary-700 text-sm">
              Manage Team Members
            </Link>
          </div>
          
          {/* Assets */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Assets</h2>
            
            <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
              {assets.length === 0 ? (
                <p className="text-gray-500">No assets found. Add assets in the Management section.</p>
              ) : (
                assets.map(asset => (
                  <div key={asset.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`asset-${asset.id}`}
                      checked={selectedAssets.includes(asset.id)}
                      onChange={() => handleAssetToggle(asset.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor={`asset-${asset.id}`} className="ml-3 block text-gray-700">
                      <span className="font-medium">{asset.name}</span>
                      <span className="text-sm text-gray-500 block">{asset.asset_type}</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            
            <Link to="/management/assets" className="text-primary-600 hover:text-primary-700 text-sm">
              Manage Assets
            </Link>
          </div>
          
          {/* Funding */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Funding</h2>
            
            <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
              {funding.length === 0 ? (
                <p className="text-gray-500">No funding found. Add funding in the Management section.</p>
              ) : (
                funding.map(fund => (
                  <div key={fund.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`fund-${fund.id}`}
                      checked={selectedFunding.includes(fund.id)}
                      onChange={() => handleFundingToggle(fund.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor={`fund-${fund.id}`} className="ml-3 block text-gray-700">
                      <span className="font-medium">{fund.name}</span>
                      <span className="text-sm text-gray-500 block">
                        {fund.amount ? `$${fund.amount.toLocaleString()}` : 'Amount not specified'} - {fund.source}
                      </span>
                    </label>
                  </div>
                ))
              )}
            </div>
            
            <Link to="/management/funding" className="text-primary-600 hover:text-primary-700 text-sm">
              Manage Funding
            </Link>
          </div>
          
          {/* Patents */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Patents</h2>
            
            <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
              {patents.length === 0 ? (
                <p className="text-gray-500">No patents found. Add patents in the Management section.</p>
              ) : (
                patents.map(patent => (
                  <div key={patent.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`patent-${patent.id}`}
                      checked={selectedPatents.includes(patent.id)}
                      onChange={() => handlePatentToggle(patent.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor={`patent-${patent.id}`} className="ml-3 block text-gray-700">
                      <span className="font-medium">{patent.name}</span>
                      {patent.patent_number && (
                        <span className="text-sm text-gray-500 block">Patent #{patent.patent_number}</span>
                      )}
                    </label>
                  </div>
                ))
              )}
            </div>
            
            <Link to="/management/patents" className="text-primary-600 hover:text-primary-700 text-sm">
              Manage Patents
            </Link>
          </div>
          
          {/* Technology Platforms */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Technology Platforms</h2>
            
            <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
              {technologies.length === 0 ? (
                <p className="text-gray-500">No technology platforms found. Add technology platforms in the Management section.</p>
              ) : (
                technologies.map(tech => (
                  <div key={tech.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`tech-${tech.id}`}
                      checked={selectedTechnologies.includes(tech.id)}
                      onChange={() => handleTechnologyToggle(tech.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor={`tech-${tech.id}`} className="ml-3 block text-gray-700">
                      <span className="font-medium">{tech.name}</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            
            <Link to="/management/technologies" className="text-primary-600 hover:text-primary-700 text-sm">
              Manage Technology Platforms
            </Link>
          </div>
          
          {/* Resource Management */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Resource Management</h2>
            <p className="text-gray-600 mb-4">
              Associate resources with your product to create a comprehensive view of your project. These resources will be included in your pitch deck and financial analysis.
            </p>
            <Link to="/management" className="btn-secondary w-full text-center">
              Manage All Resources
            </Link>
          </div>
        </div>
        
        <div className="flex justify-between mt-6">
          <Link
            to={`/products/${id}/financial`}
            className="btn-secondary"
            disabled={isSaving}
          >
            Back to Financial
          </Link>
          
          <button
            type="submit"
            className="btn-primary"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save and Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamAssetsInputPage; 